import { useEffect, useState } from "react";

import { api } from "../lib/api";
import type { TraderJournalEntry } from "../lib/types";

/**
 * Live feed of the head-trader agent's journal entries.
 *
 * The bot's `meta-trader-agent` routine fires 4× during RTH and
 * writes a markdown assessment of cross-strategy state — regime,
 * roll-call, concentration risks, anything flagged. Each fire
 * pushes here; this panel renders the most recent N entries.
 *
 * Latest entry is expanded by default. Older entries collapse to
 * headline only, click to expand.
 */

const REGIME_COLOR: Record<string, string> = {
  BULLISH: "bg-emerald-900/40 text-emerald-300",
  NEUTRAL: "bg-slate-700/40 text-slate-300",
  BEARISH: "bg-rose-900/40 text-rose-300",
};

function formatRelative(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const deltaMin = Math.round((Date.now() - t) / 60_000);
  if (deltaMin < 1) return "just now";
  if (deltaMin < 60) return `${deltaMin} min ago`;
  if (deltaMin < 60 * 24) return `${Math.round(deltaMin / 60)}h ago`;
  return `${Math.round(deltaMin / (60 * 24))}d ago`;
}

function JournalEntry({
  entry,
  isLatest,
}: {
  entry: TraderJournalEntry;
  isLatest: boolean;
}) {
  const [open, setOpen] = useState(isLatest);
  const regimeClass =
    (entry.regime && REGIME_COLOR[entry.regime]) ?? "bg-slate-800 text-slate-400";
  return (
    <li className="border-b border-ink-700/50 last:border-b-0">
      <button
        className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-ink-800/40"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-xs text-slate-500">
            {entry.entry_at_utc.slice(11, 16)}Z
          </span>
          {entry.regime && (
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${regimeClass}`}
            >
              {entry.regime}
            </span>
          )}
          <span className="text-slate-200">{entry.headline}</span>
          {entry.flags.length > 0 && (
            <span className="rounded bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-300">
              ⚑ {entry.flags.length}
            </span>
          )}
        </div>
        <span className="font-mono text-[10px] text-slate-600">
          {formatRelative(entry.entry_at_utc)}
        </span>
      </button>
      {open && (
        <div className="border-t border-ink-700/30 bg-ink-800/30 px-3 py-3 text-xs leading-relaxed text-slate-300">
          {entry.flags.length > 0 && (
            <div className="mb-2 rounded border-l-2 border-amber-600 bg-amber-900/20 px-2 py-1.5">
              <div className="mb-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-400">
                Flagged
              </div>
              <ul className="space-y-0.5 text-amber-200">
                {entry.flags.map((f, i) => (
                  <li key={i}>• {f}</li>
                ))}
              </ul>
            </div>
          )}
          <pre className="whitespace-pre-wrap font-sans">{entry.body_md}</pre>
        </div>
      )}
    </li>
  );
}

export function TraderJournalPanel({ limit = 8 }: { limit?: number }) {
  const [entries, setEntries] = useState<TraderJournalEntry[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.traderJournal(limit);
        if (!cancelled) {
          setEntries(r.rows);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "failed");
      }
    };
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [limit]);

  return (
    <section className="rounded-lg border border-ink-700 bg-ink-900">
      <div className="flex items-baseline justify-between border-b border-ink-700 px-3 py-2">
        <div className="text-sm">
          <span className="font-semibold text-slate-200">
            🧠 Head trader journal
          </span>
          <span className="ml-2 text-xs font-normal text-slate-500">
            ({entries.length} entries)
          </span>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">
          updates every 30s
        </div>
      </div>
      {err && (
        <div className="border-b border-ink-700 px-3 py-2 text-xs text-accent-err">
          {err}
        </div>
      )}
      {entries.length === 0 ? (
        <div className="px-3 py-6 text-center text-sm text-slate-500">
          No journal entries yet.
          <div className="mt-1 text-xs">
            The head-trader agent runs 4× during RTH (15:30 / 17:30 /
            19:30 / 21:30 CEST). Entries appear here as the agent
            assesses cross-strategy state.
          </div>
        </div>
      ) : (
        <ul>
          {entries.map((e, i) => (
            <JournalEntry
              key={e.entry_at_utc}
              entry={e}
              isLatest={i === 0}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
